import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import type { AccountListResponse } from '@/bindings/AccountListResponse'
import type { BranchQr } from '@/bindings/BranchQr'
import type { BranchInfo } from '@/bindings/BranchInfo'
import type { BranchMenu } from '@/bindings/BranchMenu'
import type { BranchSettings } from '@/bindings/BranchSettings'
import type { ChangeTableRequest } from '@/bindings/ChangeTableRequest'
import type { CreateOrderRequest } from '@/bindings/CreateOrderRequest'
import type { DiningTable } from '@/bindings/DiningTable'
import type { Language } from '@/bindings/Language'
import type { Menu } from '@/bindings/Menu'
import type { MoveOrderRequest } from '@/bindings/MoveOrderRequest'
import type { OrderSearchResponse } from '@/bindings/OrderSearchResponse'
import type { OrderStatus } from '@/bindings/OrderStatus'
import type { QueueOrder } from '@/bindings/QueueOrder'
import type { QueueResponse } from '@/bindings/QueueResponse'
import type { RealWeightRequest } from '@/bindings/RealWeightRequest'
import type { ReturnLineRequest } from '@/bindings/ReturnLineRequest'
import type { LanguageCoverage } from '@/bindings/LanguageCoverage'
import type { MyRestaurantsResponse } from '@/bindings/MyRestaurantsResponse'
import type { RestaurantBranchesResponse } from '@/bindings/RestaurantBranchesResponse'
import type { RestaurantContext } from '@/bindings/RestaurantContext'
import type { RestaurantRole } from '@/bindings/RestaurantRole'
import type { SessionDetails } from '@/bindings/SessionDetails'
import type { SessionListResponse } from '@/bindings/SessionListResponse'
import type { StaffOrderingContext } from '@/bindings/StaffOrderingContext'
import type { TableListResponse } from '@/bindings/TableListResponse'
import type { BestsellersResponse } from '@/bindings/BestsellersResponse'
import type { OrderListResponse } from '@/bindings/OrderListResponse'
import type { Statistics } from '@/bindings/Statistics'
import type { OrderSearchCriteria, SessionSearchCriteria } from '@/features/back-office/search-params'
import type { Span } from '@/features/back-office/statistics-params'
import { request } from '@/lib/api'
import { PAGE_SIZE } from '@/lib/paging'

/** Restaurant-session side of the restaurant API (E6); the platform side lives in `features/restaurants`. */
export const backOfficeKeys = {
  mine: ['back-office', 'mine'] as const,
  context: (slug: string) => ['back-office', 'context', slug] as const,
  coverage: (id: string, language: Language) => ['back-office', 'coverage', id, language] as const,
  branches: (id: string) => ['back-office', 'branches', id] as const,
  /** Prefix of every roster page, so one invalidation refreshes them all (E7). */
  accounts: (id: string) => ['back-office', 'accounts', id] as const,
  accountList: (id: string, role: RestaurantRole, page: number) =>
    ['back-office', 'accounts', id, 'list', role, page] as const,
  /** Prefix of every staff page of a branch (H8), so one invalidation refreshes them all. */
  branchStaff: (id: string, branchId: string) => ['back-office', 'branch-staff', id, branchId] as const,
  branchStaffList: (id: string, branchId: string, page: number) =>
    ['back-office', 'branch-staff', id, branchId, 'list', page] as const,
  menu: (id: string) => ['back-office', 'menu', id] as const,
  branchMenu: (id: string, branchId: string) => ['back-office', 'branch-menu', id, branchId] as const,
  /** Prefix of every table page and single table of a branch, so one invalidation refreshes them all (G3). */
  tables: (id: string, branchId: string) => ['back-office', 'tables', id, branchId] as const,
  tableList: (id: string, branchId: string, page: number) => ['back-office', 'tables', id, branchId, 'list', page] as const,
  /** Every table of a branch in one entry (G11's change-table picker). */
  tableAll: (id: string, branchId: string) => ['back-office', 'tables', id, branchId, 'all'] as const,
  table: (id: string, branchId: string, tableId: string) => ['back-office', 'tables', id, branchId, 'one', tableId] as const,
  branchQr: (id: string, branchId: string) => ['back-office', 'branch-qr', id, branchId] as const,
  branchSettings: (id: string, branchId: string) => ['back-office', 'branch-settings', id, branchId] as const,
  branchInfo: (id: string, branchId: string) => ['back-office', 'branch-info', id, branchId] as const,
  queue: (id: string, branchId: string) => ['back-office', 'queue', id, branchId] as const,
  /** Prefix of every order page of a branch (a table change renames their seating). */
  orders: (id: string, branchId: string) => ['back-office', 'order', id, branchId] as const,
  /** One order's details page (G10); the edits patch it from their responses. */
  order: (id: string, branchId: string, orderId: string) => ['back-office', 'order', id, branchId, orderId] as const,
  /** The staff create-order page's menu (G12); refetched after a refused placement like the customer context. */
  staffOrdering: (id: string, branchId: string) => ['back-office', 'staff-ordering', id, branchId] as const,
  /** Prefix of the sessions list and every session page of a branch, so one hint refreshes them all (G11). */
  sessions: (id: string, branchId: string) => ['back-office', 'sessions', id, branchId] as const,
  sessionList: (id: string, branchId: string) => ['back-office', 'sessions', id, branchId, 'list'] as const,
  session: (id: string, branchId: string, sessionId: string) => ['back-office', 'sessions', id, branchId, 'one', sessionId] as const,
  /** One search result, keyed by its query string (G13). */
  search: (id: string, branchId: string, what: 'orders' | 'sessions', query: string) => ['back-office', 'search', id, branchId, what, query] as const,
  /** One statistics read, keyed by its query string (H5). */
  statistics: (id: string, branchId: string, what: 'overview' | 'bestsellers' | 'orders', query: string) =>
    ['back-office', 'statistics', id, branchId, what, query] as const,
}

/** The poll fallback behind G5's hint stream (T7); the stream only makes updates prompt. */
export const QUEUE_REFETCH_MS = 45_000

export function restaurantPath(id: string, suffix = '') {
  return `/restaurants/${id}${suffix}`
}

export function myRestaurantsQuery() {
  return queryOptions({
    queryKey: backOfficeKeys.mine,
    queryFn: () => request<MyRestaurantsResponse>('/restaurants/mine'),
  })
}

/** The scope's one load: settings for the shell plus the caller's role. */
/** Keyed by slug: the manage URLs carry slugs, and this is where they become ids. */
export function restaurantContextQuery(slug: string) {
  return queryOptions({
    queryKey: backOfficeKeys.context(slug),
    queryFn: () => request<RestaurantContext>(`/restaurants/by-slug/${slug}`),
  })
}

/** T4.4: what would fall back if `language` became main. Fresh per ask. */
export function languageCoverageQuery(id: string, language: Language) {
  return queryOptions({
    queryKey: backOfficeKeys.coverage(id, language),
    queryFn: () =>
      request<LanguageCoverage>(restaurantPath(id, `/language-coverage?language=${encodeURIComponent(language)}`)),
    staleTime: 0,
  })
}

/** Every branch of the restaurant (any role): the grant form's scope choices. */
export function restaurantBranchesQuery(id: string) {
  return queryOptions({
    queryKey: backOfficeKeys.branches(id),
    queryFn: () => request<RestaurantBranchesResponse>(restaurantPath(id, '/branches')),
  })
}

/** One roster page for one manageable role (E7). */
export function accountListQuery(id: string, role: RestaurantRole, page: number) {
  return queryOptions({
    queryKey: backOfficeKeys.accountList(id, role, page),
    queryFn: () =>
      request<AccountListResponse>(restaurantPath(id, `/accounts?role=${role}&page=${page}&per_page=${PAGE_SIZE}`)),
  })
}

export function accountRolePath(id: string, userId: string) {
  return restaurantPath(id, `/accounts/${userId}/role`)
}

/** One page of the staff working in a branch (H8), newest grant first. */
export function branchStaffQuery(id: string, branchId: string, page: number) {
  return queryOptions({
    queryKey: backOfficeKeys.branchStaffList(id, branchId, page),
    queryFn: () => request<AccountListResponse>(branchStaffPath(id, branchId, `?page=${page}&per_page=${PAGE_SIZE}`)),
  })
}

export function branchStaffPath(id: string, branchId: string, suffix = '') {
  return restaurantPath(id, `/branches/${branchId}/staff${suffix}`)
}

/** The whole menu in one load (F2); mutations patch this entry from their responses. */
export function menuQuery(id: string) {
  return queryOptions({
    queryKey: backOfficeKeys.menu(id),
    queryFn: () => request<Menu>(menuPath(id)),
  })
}

export function menuPath(id: string, suffix = '') {
  return restaurantPath(id, `/menu${suffix}`)
}

export function imagesPath(id: string, suffix = '') {
  return restaurantPath(id, `/images${suffix}`)
}

/** One branch's overlay on the menu (F4); mutations patch this entry from their responses. */
export function branchMenuQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.branchMenu(id, branchId),
    queryFn: () => request<BranchMenu>(branchMenuPath(id, branchId)),
  })
}

export function branchMenuPath(id: string, branchId: string, suffix = '') {
  return restaurantPath(id, `/branches/${branchId}/menu${suffix}`)
}

/** One page of a branch's tables (G3), each with the URL its QR encodes. */
export function tableListQuery(id: string, branchId: string, page: number) {
  return queryOptions({
    queryKey: backOfficeKeys.tableList(id, branchId, page),
    queryFn: () => request<TableListResponse>(tablesPath(id, branchId, `?page=${page}&per_page=${PAGE_SIZE}`)),
  })
}

/** Every table of the branch, page by page in one query (G11's change-table picker). */
export function allTablesQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.tableAll(id, branchId),
    queryFn: async () => {
      const tables: DiningTable[] = []
      for (let page = 1; ; page++) {
        const list = await request<TableListResponse>(tablesPath(id, branchId, `?page=${page}&per_page=${ALL_TABLES_PAGE}`))
        tables.push(...list.items)
        if (list.items.length === 0 || tables.length >= Number(list.total)) return tables
      }
    },
  })
}

/** Mirrors the backend's `MAX_PER_PAGE`: the fewest requests the picker can make. */
const ALL_TABLES_PAGE = 100

export function tableQuery(id: string, branchId: string, tableId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.table(id, branchId, tableId),
    queryFn: () => request<DiningTable>(tablesPath(id, branchId, `/${tableId}`)),
  })
}

/** The branch-level QR contents: restaurant page, branch page, take-away ordering. */
export function branchQrQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.branchQr(id, branchId),
    queryFn: () => request<BranchQr>(restaurantPath(id, `/branches/${branchId}/qr`)),
  })
}

export function tablesPath(id: string, branchId: string, suffix = '') {
  return restaurantPath(id, `/branches/${branchId}/tables${suffix}`)
}

/** One branch's shop switch and ordering policy (G4); both writes patch this entry from their responses. */
export function branchSettingsQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.branchSettings(id, branchId),
    queryFn: () => request<BranchSettings>(branchSettingsPath(id, branchId)),
  })
}

export function branchSettingsPath(id: string, branchId: string, suffix = '') {
  return restaurantPath(id, `/branches/${branchId}/settings${suffix}`)
}

/** One branch's name, address, phone, hours and announcement (H2); the save patches this entry from its response. */
export function branchInfoQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.branchInfo(id, branchId),
    queryFn: () => request<BranchInfo>(branchInfoPath(id, branchId)),
  })
}

export function branchInfoPath(id: string, branchId: string) {
  return restaurantPath(id, `/branches/${branchId}/info`)
}

/** Every active order of the branch, oldest first (G9); the page filters, so the counts per status are always there. */
export function queueQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.queue(id, branchId),
    queryFn: () => request<QueueResponse>(queuePath(id, branchId)),
    refetchInterval: QUEUE_REFETCH_MS,
  })
}

export function queuePath(id: string, branchId: string, suffix = '') {
  return restaurantPath(id, `/branches/${branchId}/queue${suffix}`)
}

/** Staff move one step or cancel (T16); `ORDER_MOVED` names the status the order is in instead. */
export function moveOrder(id: string, branchId: string, orderId: string, body: MoveOrderRequest) {
  return request<QueueOrder>(restaurantPath(id, `/branches/${branchId}/orders/${orderId}/status`), { method: 'POST', body })
}

/** One order as the queue's ticket shape, history included (G10); polled behind the hints like the queue. */
export function orderQuery(id: string, branchId: string, orderId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.order(id, branchId, orderId),
    queryFn: () => request<QueueOrder>(orderPath(id, branchId, orderId)),
    refetchInterval: QUEUE_REFETCH_MS,
  })
}

export function orderPath(id: string, branchId: string, orderId: string, suffix = '') {
  return restaurantPath(id, `/branches/${branchId}/orders/${orderId}${suffix}`)
}

/** A return on one line (T16: any order, forever); `INVALID_RETURN` carries the step and what is left. */
export function returnLine(id: string, branchId: string, orderId: string, lineId: string, body: ReturnLineRequest) {
  return request<QueueOrder>(orderPath(id, branchId, orderId, `/lines/${lineId}/return`), { method: 'POST', body })
}

/** The real weight of a by-weight line (T16: active orders only); `ORDER_MOVED` says where the order went instead. */
export function setRealWeight(id: string, branchId: string, orderId: string, lineId: string, body: RealWeightRequest) {
  return request<QueueOrder>(orderPath(id, branchId, orderId, `/lines/${lineId}/weight`), { method: 'POST', body })
}

/** The branch's open sessions with their bills, oldest first (G11); polled behind the hints like the queue. */
export function sessionListQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.sessionList(id, branchId),
    queryFn: () => request<SessionListResponse>(sessionsPath(id, branchId)),
    refetchInterval: QUEUE_REFETCH_MS,
  })
}

/** One session with every order as a ticket, closed ones included (G11). */
export function sessionQuery(id: string, branchId: string, sessionId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.session(id, branchId, sessionId),
    queryFn: () => request<SessionDetails>(sessionsPath(id, branchId, `/${sessionId}`)),
    refetchInterval: QUEUE_REFETCH_MS,
  })
}

export function sessionsPath(id: string, branchId: string, suffix = '') {
  return restaurantPath(id, `/branches/${branchId}/sessions${suffix}`)
}

/** Close a session (T16: no active orders); `SESSION_HAS_ACTIVE_ORDERS` carries the count, `SESSION_CLOSED` says it already is. */
export function closeSession(id: string, branchId: string, sessionId: string) {
  return request<SessionDetails>(sessionsPath(id, branchId, `/${sessionId}/close`), { method: 'POST', body: {} })
}

/**
 * Move a session to another table; `TABLE_BUSY` names a table that already seats a party, and
 * `merge: true` joins it instead — the answer is then the target session, not this one.
 */
export function changeTable(id: string, branchId: string, sessionId: string, body: ChangeTableRequest) {
  return request<SessionDetails>(sessionsPath(id, branchId, `/${sessionId}/table`), { method: 'POST', body })
}

/** The branch's orderable menu for the staff create-order page (G12): effective prices, sold-out flags, the address field. */
export function staffOrderingQuery(id: string, branchId: string) {
  return queryOptions({
    queryKey: backOfficeKeys.staffOrdering(id, branchId),
    queryFn: () => request<StaffOrderingContext>(restaurantPath(id, `/branches/${branchId}/ordering`)),
  })
}

/** Staff place an order (G12): 201 with the ticket, or 200 with the first result on a replayed key. */
export function createOrder(id: string, branchId: string, body: CreateOrderRequest) {
  return request<QueueOrder>(restaurantPath(id, `/branches/${branchId}/orders`), { method: 'POST', body })
}

// -- search (G13) --

/** The order the criteria name, or `order: null`: exact by code, or by daily number + working date. */
export function searchOrderQuery(id: string, branchId: string, criteria: OrderSearchCriteria) {
  const query = new URLSearchParams(criteria).toString()
  return queryOptions({
    queryKey: backOfficeKeys.search(id, branchId, 'orders', query),
    queryFn: () => request<OrderSearchResponse>(restaurantPath(id, `/branches/${branchId}/search/orders?${query}`)),
  })
}

/** Sessions newest first: exact by code (at most one), or a table's seatings of a date, at a time when given. */
export function searchSessionsQuery(id: string, branchId: string, criteria: SessionSearchCriteria) {
  const query = new URLSearchParams(criteria).toString()
  return queryOptions({
    queryKey: backOfficeKeys.search(id, branchId, 'sessions', query),
    queryFn: () => request<SessionListResponse>(restaurantPath(id, `/branches/${branchId}/search/sessions?${query}`)),
  })
}

// -- statistics (H5) --

/** The overview of a span of working dates: sales, status, type, category, top-5 items. */
export function statisticsQuery(id: string, branchId: string, span: Span) {
  const query = new URLSearchParams({ from: span.from, to: span.to }).toString()
  return queryOptions({
    queryKey: backOfficeKeys.statistics(id, branchId, 'overview', query),
    queryFn: () => request<Statistics>(restaurantPath(id, `/branches/${branchId}/statistics?${query}`)),
    placeholderData: keepPreviousData,
  })
}

/** Every item sold in the span, per category, best-selling first. */
export function bestsellersQuery(id: string, branchId: string, span: Span) {
  const query = new URLSearchParams({ from: span.from, to: span.to }).toString()
  return queryOptions({
    queryKey: backOfficeKeys.statistics(id, branchId, 'bestsellers', query),
    queryFn: () => request<BestsellersResponse>(restaurantPath(id, `/branches/${branchId}/statistics/bestsellers?${query}`)),
  })
}

/** The orders of one working date by number, a page at a time, every status or just `status`. */
export function ordersOfDateQuery(id: string, branchId: string, date: string, page: number, status?: OrderStatus) {
  const query = new URLSearchParams({ date, ...(status ? { status } : {}), page: String(page), per_page: String(PAGE_SIZE) }).toString()
  return queryOptions({
    queryKey: backOfficeKeys.statistics(id, branchId, 'orders', query),
    queryFn: () => request<OrderListResponse>(restaurantPath(id, `/branches/${branchId}/statistics/orders?${query}`)),
    placeholderData: keepPreviousData,
  })
}
